import { memo, useCallback, useEffect, useMemo, type FunctionComponent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
// Material UI imports
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
// Skillmore UI Components
import { mapChunks } from '@empathco/ui-components/src/helpers/intl';
import useMutationMethod from '@empathco/ui-components/src/hooks/useMutationMethod';
import ActionSnackbar from '@empathco/ui-components/src/elements/ActionSnackbar';
// local imports
import { PROFILE_BUILDER_COMPLETE } from '../graphql/ProfileBuilderComplete';
import { ProfileBuilderCompleteDocument } from '../graphql/types';
import { PATH_MY } from '../config/paths';
// SCSS imports
import { root, button } from './BuilderCompleteStep.module.scss';

type BuilderCompleteStepProps = {
  readonly testComplete?: boolean | null;
}

const BuilderCompleteStep: FunctionComponent<BuilderCompleteStepProps> = ({
  testComplete = false
}) => {
  const navigate = useNavigate();

  const { mutate: builderComplete, loading: completePending, failed: completeFailed } = useMutationMethod({
    key: 'profileBuilderComplete',
    mutation: useMutation(PROFILE_BUILDER_COMPLETE as typeof ProfileBuilderCompleteDocument)
  });

  const infoValues = useMemo(() => ({
    // eslint-disable-next-line react/no-unstable-nested-components
    line: (chunks?: ReactNode | null): ReactNode => (
      <Typography variant="h3" paragraph>
        {mapChunks(chunks)}
      </Typography>
    )
  }), []);

  const handleComplete = useCallback(() => {
    builderComplete({
      variables: { input: {} },
      onCompleted: (data) => {
        if (data?.profileBuilderComplete?.success) navigate(PATH_MY);
      }
    });
  }, [builderComplete, navigate]);

  // for Storybook & Jest-snapshots testing only
  useEffect(() => {
    if (testComplete) builderComplete({ variables: { input: {} } });
  }, [testComplete, builderComplete]);

  return (
    <>
      <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          className={root}
      >
        <Box p={{ xs: 3, sm: 6 }} textAlign="center">
          <FormattedMessage id="builder.complete.info" values={infoValues}/>
        </Box>
        <Box pb={6} justifyContent="center">
          <Button
              color="primary"
              variant="contained"
              disableElevation
              disabled={completePending}
              startIcon={completePending ? <CircularProgress size={18} color="inherit"/> : undefined}
              className={button}
              onClick={handleComplete}
          >
            <FormattedMessage id="builder.start_exploring"/>
          </Button>
        </Box>
      </Box>
      <ActionSnackbar
          severity="error"
          message="builder.complete.error"
          open={completeFailed}
      />
    </>
  );
};

export default memo(BuilderCompleteStep);
